import { Provider } from '@angular/core';
import {
  AUTH_DATA_SERVICE,
  InMemoryAuthDataService
} from '@pharvio/accounts/admin-account/data-access';
import {
  USER_DATA_SERVICE,
  ROLE_DATA_SERVICE,
  InMemoryRoleDataService,
  InMemoryUserDataService
} from '@pharvio/users/data-access';
import {
  CATEGORIES_DATA_SERVICE,
  TAGS_DATA_SERVICE,
  SUPPLIERS_DATA_SERVICE,
  BATCHES_DATA_SERVICE,
  PRODUCTS_DATA_SERVICE,
  FEATURED_PRODUCTS_DATA_SERVICE,
  IMPORT_DATA_SERVICE,
  InMemoryBatchesDataService,
  InMemoryCategoriesDataService,
  InMemoryFeaturedProductsDataService,
  InMemoryImportDataService,
  InMemoryProductDataService,
  InMemorySuppliersDataService,
  InMemoryTagsDataService
} from '@pharvio/catalog/data-access';
import {
  BOOKABLE_SERVICES_DATA_SERVICE,
  BOOKINGS_DATA_SERVICE,
  HttpBookableServicesDataService,
  HttpBookingsDataService,
  InMemoryBookableServicesDataService,
  InMemoryBookingsDataService
} from '@pharvio/bookings/data-access';
import {
  CUSTOMER_DATA_SERVICE,
  InMemoryCustomerDataService
} from '@pharvio/customers/data-access';
import {
  EXPENSES_DATA_SERVICE,
  EXPENSE_CATEGORIES_DATA_SERVICE,
  EXPENSE_IMPORT_DATA_SERVICE,
  EXPENSE_SCHEDULES_DATA_SERVICE,
  HttpExpenseCategoriesDataService,
  HttpExpenseImportDataService,
  HttpExpenseSchedulesDataService,
  HttpExpensesDataService,
  HttpPayeesDataService,
  InMemoryExpenseCategoriesDataService,
  InMemoryExpenseImportDataService,
  InMemoryExpenseSchedulesDataService,
  InMemoryExpensesDataService,
  InMemoryPayeesDataService,
  PAYEES_DATA_SERVICE
} from '@pharvio/expenses/data-access';
import {
  GRN_DATA_SERVICE,
  PURCHASE_ORDERS_DATA_SERVICE,
  RECALLS_DATA_SERVICE,
  STOCK_DATA_SERVICE,
  STOCK_TAKES_DATA_SERVICE,
  TRANSFERS_DATA_SERVICE,
  InMemoryGrnDataService,
  InMemoryPurchaseOrdersDataService,
  InMemoryRecallsDataService,
  InMemoryStockDataService,
  InMemoryStockTakesDataService,
  InMemoryTransfersDataService
} from '@pharvio/inventory/data-access';
import {
  InMemoryPosDataService,
  POS_DATA_SERVICE
} from '@pharvio/pos/data-access';
import {
  InMemoryLoyaltyDataService,
  LOYALTY_DATA_SERVICE
} from '@pharvio/loyalty/data-access';
import {
  InMemoryReportsDataService,
  REPORTS_DATA_SERVICE
} from '@pharvio/reports/data-access';
import {
  InMemoryPrescriptionDataService,
  PRESCRIPTION_DATA_SERVICE
} from '@pharvio/prescriptions/data-access';
import {
  InMemoryOrderDataService,
  InMemoryReturnDataService,
  ORDER_DATA_SERVICE,
  RETURN_DATA_SERVICE
} from '@pharvio/orders/data-access';
import {
  ASSISTANT_DATA_SERVICE,
  InMemoryAssistantDataService
} from '@pharvio/assistant/data-access';
import {
  HttpTaxClassesDataService,
  FEATURES_DATA_SERVICE,
  HttpFeaturesDataService,
  InMemoryFeaturesDataService,
  InMemorySettingsDataService,
  InMemoryTaxClassesDataService,
  SETTINGS_DATA_SERVICE,
  TAX_CLASSES_DATA_SERVICE
} from '@pharvio/settings/data-access';
import {
  STOREFRONT_DATA_SERVICE,
  HttpStorefrontDataService,
  STOREFRONT_AVAILABILITY_DATA_SERVICE,
  HttpStorefrontAvailabilityDataService,
  InMemoryStorefrontDataService,
  InMemoryStorefrontAvailabilityDataService
} from '@pharvio/storefront/data-access';

import { HttpAuthDataService } from '@pharvio/accounts/admin-account/data-access';
import {
  HttpUserDataService,
  HttpRoleDataService
} from '@pharvio/users/data-access';
import {
  HttpCategoriesDataService,
  HttpTagsDataService,
  HttpSuppliersDataService,
  HttpBatchesDataService,
  HttpProductDataService,
  HttpFeaturedProductsDataService,
  HttpImportDataService
} from '@pharvio/catalog/data-access';
import { HttpCustomerDataService } from '@pharvio/customers/data-access';
import {
  HttpGrnDataService,
  HttpPurchaseOrdersDataService,
  HttpRecallsDataService,
  HttpStockDataService,
  HttpStockTakesDataService,
  HttpTransfersDataService
} from '@pharvio/inventory/data-access';
import { HttpPosDataService } from '@pharvio/pos/data-access';
import { HttpLoyaltyDataService } from '@pharvio/loyalty/data-access';
import { HttpReportsDataService } from '@pharvio/reports/data-access';
import { HttpPrescriptionDataService } from '@pharvio/prescriptions/data-access';
import {
  HttpOrderDataService,
  HttpReturnDataService
} from '@pharvio/orders/data-access';
import { HttpAssistantDataService } from '@pharvio/assistant/data-access';
import { HttpSettingsDataService } from '@pharvio/settings/data-access';
import {
  BRANCHES_DATA_SERVICE,
  HttpBranchesDataService,
  InMemoryBranchesDataService
} from '@pharvio/branches/data-access';
import {
  TENANT_ORGANISATION_DATA_SERVICE,
  HttpTenantOrganisationDataService,
  InMemoryTenantOrganisationDataService
} from '@pharvio/tenant-organisation/data-access';
import {
  BRANCH_SUPERINTENDENTS_DATA_SERVICE,
  HttpBranchSuperintendentsDataService,
  InMemoryBranchSuperintendentsDataService
} from '@pharvio/branch-superintendents/data-access';
import {
  USER_CREDENTIALS_DATA_SERVICE,
  HttpUserCredentialsDataService,
  InMemoryUserCredentialsDataService
} from '@pharvio/user-credentials/data-access';
import {
  PAYOUT_ACCOUNTS_DATA_SERVICE,
  HttpPayoutAccountsDataService,
  InMemoryPayoutAccountsDataService
} from '@pharvio/payouts/data-access';
import {
  CATALOG_SYNC_SOURCE,
  CUSTOMER_SYNC_SOURCE,
  HttpCustomerSyncSource,
  HttpOfflineSessionDataService,
  HttpOfflineSyncDataService,
  HttpSnapshotCatalogSyncSource,
  InMemoryCatalogSyncSource,
  InMemoryCustomerSyncSource,
  InMemoryOfflineSessionDataService,
  InMemoryOfflineSyncDataService,
  OFFLINE_SESSION_DATA_SERVICE,
  OFFLINE_SYNC_DATA_SERVICE
} from '@pharvio/offline';

export function provideDataServices(): Provider[] {
  return [
    {
      // Offline catalog sync over the BE keyset endpoints
      // (.docs/ERP/tasks/off-tasks/offline_mode_api.md). LegacyHttpCatalogSyncSource
      // remains available as a /v1/products fallback.
      provide: CATALOG_SYNC_SOURCE,
      useClass: HttpSnapshotCatalogSyncSource
    },
    {
      provide: CUSTOMER_SYNC_SOURCE,
      useClass: HttpCustomerSyncSource
    },
    {
      provide: OFFLINE_SESSION_DATA_SERVICE,
      useClass: HttpOfflineSessionDataService
    },
    {
      provide: OFFLINE_SYNC_DATA_SERVICE,
      useClass: HttpOfflineSyncDataService
    },
    {
      provide: BRANCHES_DATA_SERVICE,
      useClass: HttpBranchesDataService
    },
    {
      provide: AUTH_DATA_SERVICE,
      useClass: HttpAuthDataService
    },
    {
      provide: USER_DATA_SERVICE,
      useClass: HttpUserDataService
    },
    {
      provide: ROLE_DATA_SERVICE,
      useClass: HttpRoleDataService
    },
    {
      provide: CATEGORIES_DATA_SERVICE,
      useClass: HttpCategoriesDataService
    },
    {
      provide: TAGS_DATA_SERVICE,
      useClass: HttpTagsDataService
    },
    {
      provide: SUPPLIERS_DATA_SERVICE,
      useClass: HttpSuppliersDataService
    },
    {
      provide: CUSTOMER_DATA_SERVICE,
      useClass: HttpCustomerDataService
    },
    {
      provide: PURCHASE_ORDERS_DATA_SERVICE,
      useClass: HttpPurchaseOrdersDataService
    },
    {
      provide: GRN_DATA_SERVICE,
      useClass: HttpGrnDataService
    },
    {
      provide: STOCK_DATA_SERVICE,
      useClass: HttpStockDataService
    },
    {
      provide: STOCK_TAKES_DATA_SERVICE,
      useClass: HttpStockTakesDataService
    },
    {
      provide: TRANSFERS_DATA_SERVICE,
      useClass: HttpTransfersDataService
    },
    {
      provide: RECALLS_DATA_SERVICE,
      useClass: HttpRecallsDataService
    },
    {
      provide: POS_DATA_SERVICE,
      useClass: HttpPosDataService
    },
    {
      provide: LOYALTY_DATA_SERVICE,
      useClass: HttpLoyaltyDataService
    },
    {
      provide: REPORTS_DATA_SERVICE,
      useClass: HttpReportsDataService
    },
    {
      provide: PRESCRIPTION_DATA_SERVICE,
      useClass: HttpPrescriptionDataService
    },
    {
      provide: ORDER_DATA_SERVICE,
      useClass: HttpOrderDataService
    },
    {
      provide: RETURN_DATA_SERVICE,
      useClass: HttpReturnDataService
    },
    {
      provide: ASSISTANT_DATA_SERVICE,
      useClass: HttpAssistantDataService
    },
    {
      provide: SETTINGS_DATA_SERVICE,
      useClass: HttpSettingsDataService
    },
    {
      provide: EXPENSES_DATA_SERVICE,
      useClass: HttpExpensesDataService
    },
    {
      provide: EXPENSE_CATEGORIES_DATA_SERVICE,
      useClass: HttpExpenseCategoriesDataService
    },
    {
      provide: PAYEES_DATA_SERVICE,
      useClass: HttpPayeesDataService
    },
    {
      provide: EXPENSE_SCHEDULES_DATA_SERVICE,
      useClass: HttpExpenseSchedulesDataService
    },
    {
      provide: EXPENSE_IMPORT_DATA_SERVICE,
      useClass: HttpExpenseImportDataService
    },
    {
      provide: TAX_CLASSES_DATA_SERVICE,
      useClass: HttpTaxClassesDataService
    },
    {
      provide: FEATURES_DATA_SERVICE,
      useClass: HttpFeaturesDataService
    },
    {
      provide: STOREFRONT_DATA_SERVICE,
      useClass: HttpStorefrontDataService
    },
    {
      provide: STOREFRONT_AVAILABILITY_DATA_SERVICE,
      useClass: HttpStorefrontAvailabilityDataService
    },
    {
      provide: PRODUCTS_DATA_SERVICE,
      useClass: HttpProductDataService
    },
    {
      provide: BATCHES_DATA_SERVICE,
      useClass: HttpBatchesDataService
    },
    {
      provide: FEATURED_PRODUCTS_DATA_SERVICE,
      useClass: HttpFeaturedProductsDataService
    },
    {
      provide: IMPORT_DATA_SERVICE,
      useClass: HttpImportDataService
    },
    {
      provide: TENANT_ORGANISATION_DATA_SERVICE,
      useClass: HttpTenantOrganisationDataService
    },
    {
      provide: BRANCH_SUPERINTENDENTS_DATA_SERVICE,
      useClass: HttpBranchSuperintendentsDataService
    },
    {
      provide: USER_CREDENTIALS_DATA_SERVICE,
      useClass: HttpUserCredentialsDataService
    },
    {
      provide: PAYOUT_ACCOUNTS_DATA_SERVICE,
      useClass: HttpPayoutAccountsDataService
    },
    {
      provide: BOOKABLE_SERVICES_DATA_SERVICE,
      useClass: HttpBookableServicesDataService
    },
    {
      provide: BOOKINGS_DATA_SERVICE,
      useClass: HttpBookingsDataService
    }
  ];
}

export function provideInMemoryDataServices(): Provider[] {
  return [
    {
      provide: CATALOG_SYNC_SOURCE,
      useClass: InMemoryCatalogSyncSource
    },
    {
      provide: CUSTOMER_SYNC_SOURCE,
      useClass: InMemoryCustomerSyncSource
    },
    {
      provide: OFFLINE_SESSION_DATA_SERVICE,
      useClass: InMemoryOfflineSessionDataService
    },
    {
      provide: OFFLINE_SYNC_DATA_SERVICE,
      useClass: InMemoryOfflineSyncDataService
    },
    {
      provide: BRANCHES_DATA_SERVICE,
      useClass: InMemoryBranchesDataService
    },
    {
      provide: AUTH_DATA_SERVICE,
      useClass: InMemoryAuthDataService
    },
    {
      provide: USER_DATA_SERVICE,
      useClass: InMemoryUserDataService
    },
    {
      provide: ROLE_DATA_SERVICE,
      useClass: InMemoryRoleDataService
    },
    {
      provide: CATEGORIES_DATA_SERVICE,
      useClass: InMemoryCategoriesDataService
    },
    {
      provide: TAGS_DATA_SERVICE,
      useClass: InMemoryTagsDataService
    },
    {
      provide: SUPPLIERS_DATA_SERVICE,
      useClass: InMemorySuppliersDataService
    },
    {
      provide: CUSTOMER_DATA_SERVICE,
      useClass: InMemoryCustomerDataService
    },
    {
      provide: PURCHASE_ORDERS_DATA_SERVICE,
      useClass: InMemoryPurchaseOrdersDataService
    },
    {
      provide: GRN_DATA_SERVICE,
      useClass: InMemoryGrnDataService
    },
    {
      provide: STOCK_DATA_SERVICE,
      useClass: InMemoryStockDataService
    },
    {
      provide: STOCK_TAKES_DATA_SERVICE,
      useClass: InMemoryStockTakesDataService
    },
    {
      provide: TRANSFERS_DATA_SERVICE,
      useClass: InMemoryTransfersDataService
    },
    {
      provide: RECALLS_DATA_SERVICE,
      useClass: InMemoryRecallsDataService
    },
    {
      provide: POS_DATA_SERVICE,
      useClass: InMemoryPosDataService
    },
    {
      provide: LOYALTY_DATA_SERVICE,
      useClass: InMemoryLoyaltyDataService
    },
    {
      provide: REPORTS_DATA_SERVICE,
      useClass: InMemoryReportsDataService
    },
    {
      provide: PRESCRIPTION_DATA_SERVICE,
      useClass: InMemoryPrescriptionDataService
    },
    {
      provide: ORDER_DATA_SERVICE,
      useClass: InMemoryOrderDataService
    },
    {
      provide: RETURN_DATA_SERVICE,
      useClass: InMemoryReturnDataService
    },
    {
      provide: ASSISTANT_DATA_SERVICE,
      useClass: InMemoryAssistantDataService
    },
    {
      provide: SETTINGS_DATA_SERVICE,
      useClass: InMemorySettingsDataService
    },
    {
      provide: EXPENSES_DATA_SERVICE,
      useClass: InMemoryExpensesDataService
    },
    {
      provide: EXPENSE_CATEGORIES_DATA_SERVICE,
      useClass: InMemoryExpenseCategoriesDataService
    },
    {
      provide: PAYEES_DATA_SERVICE,
      useClass: InMemoryPayeesDataService
    },
    {
      provide: EXPENSE_SCHEDULES_DATA_SERVICE,
      useClass: InMemoryExpenseSchedulesDataService
    },
    {
      provide: EXPENSE_IMPORT_DATA_SERVICE,
      useClass: InMemoryExpenseImportDataService
    },
    {
      provide: TAX_CLASSES_DATA_SERVICE,
      useClass: InMemoryTaxClassesDataService
    },
    {
      provide: FEATURES_DATA_SERVICE,
      useClass: InMemoryFeaturesDataService
    },
    {
      provide: STOREFRONT_DATA_SERVICE,
      useClass: InMemoryStorefrontDataService
    },
    {
      provide: STOREFRONT_AVAILABILITY_DATA_SERVICE,
      useClass: InMemoryStorefrontAvailabilityDataService
    },
    {
      provide: PRODUCTS_DATA_SERVICE,
      useClass: InMemoryProductDataService
    },
    {
      provide: BATCHES_DATA_SERVICE,
      useClass: InMemoryBatchesDataService
    },
    {
      provide: FEATURED_PRODUCTS_DATA_SERVICE,
      useClass: InMemoryFeaturedProductsDataService
    },
    {
      provide: IMPORT_DATA_SERVICE,
      useClass: InMemoryImportDataService
    },
    {
      provide: TENANT_ORGANISATION_DATA_SERVICE,
      useClass: InMemoryTenantOrganisationDataService
    },
    {
      provide: BRANCH_SUPERINTENDENTS_DATA_SERVICE,
      useClass: InMemoryBranchSuperintendentsDataService
    },
    {
      provide: USER_CREDENTIALS_DATA_SERVICE,
      useClass: InMemoryUserCredentialsDataService
    },
    {
      provide: PAYOUT_ACCOUNTS_DATA_SERVICE,
      useClass: InMemoryPayoutAccountsDataService
    },
    {
      provide: BOOKABLE_SERVICES_DATA_SERVICE,
      useClass: InMemoryBookableServicesDataService
    },
    {
      provide: BOOKINGS_DATA_SERVICE,
      useClass: InMemoryBookingsDataService
    }
  ];
}
